// src/services/extractionApi.ts
import {
  apiGet,
  apiGetById,
  apiFormPost,
  apiDelete,
  apiPost,
  apiPatch,
  apiGetFile,
  type ApiResponse,
} from './genesisApi/httpsMethods';
import type {
  Extraction,
  ExtractionListResponse,
  ExtractionStatusResponse,
  ExtractionCreateResponse,
  ExtractionFilters,
  TenantColumnRange,
  ExtractionImagesResponse,
  ExtractionImage,
} from '../types/extraction';

const EXTRACTION_ENDPOINT = 'ai-extractions';

/**
 * Create a new extraction (async)
 */
export async function createExtraction(
  file: File,
  options: {
    excel_provider?: string;
    is_term_sheet?: boolean;
    is_initial_materials?: boolean;
    assets?: string[];
    additional_prompt?: string;
    deal_id?: number;
    extract_deal?: boolean;
    extract_commentary?: boolean;
    extract_assets_tenants?: boolean;
    extract_images?: boolean;
  } = {}
): Promise<ApiResponse<ExtractionCreateResponse>> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('excel_provider', options.excel_provider || 'anthropic_column_matching');
  formData.append('is_term_sheet', String(options.is_term_sheet || false));
  formData.append(
    'is_initial_materials',
    String(options.is_initial_materials || false),
  );
  formData.append('extract_images', String(options.extract_images || false));

  if (options.assets && options.assets.length > 0) {
    formData.append('assets', JSON.stringify(options.assets));
  }
  
  if (options.additional_prompt) {
    formData.append('additional_prompt', options.additional_prompt);
  }

  if (options.deal_id) {
    formData.append('deal_id', String(options.deal_id));
  }

  // Extract flags - only send if deal_id is present (edit mode)
  if (options.deal_id !== undefined) {
    if (options.extract_deal !== undefined) {
      formData.append('extract_deal', String(options.extract_deal));
    }
    if (options.extract_commentary !== undefined) {
      formData.append('extract_commentary', String(options.extract_commentary));
    }
    if (options.extract_assets_tenants !== undefined) {
      formData.append('extract_assets_tenants', String(options.extract_assets_tenants));
    }
  }

  return apiFormPost<ExtractionCreateResponse>(EXTRACTION_ENDPOINT, formData);
}

/**
 * List all extractions with optional filters
 */
export async function listExtractions(
  filters?: ExtractionFilters
): Promise<ApiResponse<ExtractionListResponse>> {
  const queryParams: Record<string, string | number> = {};
  
  if (filters?.status) {
    queryParams.status = filters.status;
  }
  if (filters?.limit) {
    queryParams.limit = filters.limit;
  }
  if (filters?.offset) {
    queryParams.offset = filters.offset;
  }

  return apiGet<ExtractionListResponse>(EXTRACTION_ENDPOINT, queryParams);
}

/**
 * Get a specific extraction by ID
 */
export async function getExtraction(
  id: number,
  includeResults: boolean = false
): Promise<ApiResponse<Extraction>> {
  const queryParams = includeResults ? { include_results: 'true' } : undefined;
  if (queryParams) {
    return apiGet<Extraction>(`${EXTRACTION_ENDPOINT}/${id}`, queryParams);
  }
  return apiGetById<Extraction>(EXTRACTION_ENDPOINT, id);
}

/**
 * Get extraction status (lightweight endpoint for polling)
 */
export async function getExtractionStatus(
  id: number
): Promise<ApiResponse<ExtractionStatusResponse>> {
  const response = await apiGet<ExtractionStatusResponse>(
    `${EXTRACTION_ENDPOINT}/${id}/status`
  );
  return response;
}

/**
 * Delete an extraction
 */
export async function deleteExtraction(
  id: number
): Promise<ApiResponse<{ message: string }>> {
  return apiDelete<{ message: string }>(EXTRACTION_ENDPOINT, id);
}

/**
 * Download the original Excel file for an extraction
 */
export async function downloadExtractionExcel(
  id: number
): Promise<{ success: boolean; data?: Blob; message?: string }> {
  return apiGetFile(`${EXTRACTION_ENDPOINT}/${id}/excel-file`);
}

/**
 * Update column overrides and get refreshed column ranges / preview data
 */
export async function updateExtractionColumnOverrides(
  id: number,
  overrides: Record<string, string>
): Promise<
  ApiResponse<{
    tenant_column_ranges?: TenantColumnRange[];
    tenant_dataframe?: any[];
  }>
> {
  return apiPost(`${EXTRACTION_ENDPOINT}/${id}/column-overrides`, {
    column_overrides: overrides,
  });
}

/**
 * Request a fresh tenant data preview using overrides
 */
/**
 * Get extracted images for an extraction job
 */
export async function getExtractionImages(
  id: number,
): Promise<ApiResponse<ExtractionImagesResponse>> {
  return apiGet<ExtractionImagesResponse>(
    `${EXTRACTION_ENDPOINT}/${id}/images`,
  );
}

/**
 * Update selection state for an extracted image.
 */
export async function updateExtractionImage(
  extractionId: number,
  imageId: number,
  payload: { is_selected?: boolean; is_hero?: boolean; caption?: string },
): Promise<ApiResponse<ExtractionImage>> {
  return apiPatch<ExtractionImage>(
    `${EXTRACTION_ENDPOINT}/${extractionId}/images/${imageId}`,
    payload,
  );
}

/**
 * Request a fresh tenant data preview using overrides
 */
export async function getExtractionTenantDataPreview(
  id: number,
  payload: {
    tenant_column_ranges: TenantColumnRange[];
    attachment_index?: number;
    asset_names?: string[];
  }
): Promise<
  ApiResponse<{
    tenant_dataframe?: any[];
    tenant_column_ranges?: TenantColumnRange[];
    matched_assets?: Record<string, unknown>;
    asset_data?: any[];
  }>
> {
  return apiPost(`${EXTRACTION_ENDPOINT}/${id}/tenant-data-preview`, payload);
}

